/**
 * React Query hooks for Plan Features & Gating
 *
 * Provides feature gating based on tenant's subscription plan.
 * Used to show/hide premium features and encourage upgrades.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { BillingService } from '../services/billing';

// Query key factory
export const planFeaturesKeys = {
  all: ['plan-features'],
  features: () => [...planFeaturesKeys.all, 'features'],
  usageSummary: () => [...planFeaturesKeys.all, 'usage-summary'],
};

/**
 * Hook to get a memoized BillingService instance
 */
function useBillingService() {
  const { getToken } = useAuth();
  return useMemo(() => new BillingService(getToken), [getToken]);
}

// ===========================================
// PLAN FEATURES HOOK
// ===========================================

/**
 * Hook to fetch plan features for the current tenant.
 *
 * Returns feature flags indicating what the plan includes:
 * - workflow_builder: boolean
 * - cases: boolean
 * - audit_logs: boolean
 * - webhooks: boolean
 * - ai_batch_analyze: boolean
 * - max_team_members: number (-1 = unlimited)
 * - max_api_keys: number (-1 = unlimited)
 * - current_plan: string (developer, starter, professional, enterprise)
 *
 * @param {Object} options - Additional React Query options
 */
export function usePlanFeatures(options = {}) {
  const service = useBillingService();

  return useQuery({
    queryKey: planFeaturesKeys.features(),
    queryFn: ({ signal }) => service.getPlanFeatures({ signal }),
    staleTime: 300000, // 5 minutes - plan rarely changes
    retry: 2,
    // Fallback to developer plan features if API fails
    placeholderData: {
      current_plan: 'developer',
      workflow_builder: false,
      cases: false,
      audit_logs: false,
      webhooks: false,
      ai_batch_analyze: false,
      max_team_members: 1,
      max_api_keys: 1,
    },
    ...options,
  });
}

/**
 * Helper hook to check if a specific feature is available.
 *
 * @param {string} feature - Feature name (e.g., 'workflow_builder', 'cases')
 * @returns {Object} { hasFeature: boolean, plan: string, isLoading: boolean }
 */
export function useHasFeature(feature) {
  const { data, isLoading, error } = usePlanFeatures();

  if (isLoading || error) {
    return { hasFeature: false, plan: null, isLoading };
  }

  // Handle numeric limits
  if (typeof data?.[feature] === 'number') {
    return {
      hasFeature: data[feature] !== 0,
      plan: data?.current_plan,
      limit: data[feature],
      isLoading: false,
    };
  }

  return {
    hasFeature: Boolean(data?.[feature]),
    plan: data?.current_plan,
    isLoading: false,
  };
}

// ===========================================
// USAGE SUMMARY HOOK
// ===========================================

/**
 * Hook to fetch comprehensive usage summary.
 *
 * Returns:
 * - current_plan: string
 * - billing_cycle: { start, end, days_remaining }
 * - verifications: { free_used, free_limit, paid_used, total }
 * - overage_rate_cents: number
 * - estimated_overage_dollars: number
 * - subscription_status: string
 * - has_payment_method: boolean
 *
 * @param {Object} options - Additional React Query options
 */
export function useUsageSummary(options = {}) {
  const service = useBillingService();

  return useQuery({
    queryKey: planFeaturesKeys.usageSummary(),
    queryFn: ({ signal }) => service.getUsageSummary({ signal }),
    staleTime: 60000, // 1 minute - usage changes frequently
    retry: 2,
    ...options,
  });
}

/**
 * Helper hook to check usage limits and warnings.
 *
 * @returns {Object} Usage status with warning indicators
 */
export function useUsageLimits() {
  const { data, isLoading, error } = useUsageSummary();

  if (isLoading || error || !data) {
    return {
      isLoading,
      error,
      freeUsed: 0,
      freeLimit: 1000,
      percentageUsed: 0,
      isNearLimit: false,
      isAtLimit: false,
      isOverLimit: false,
      daysRemaining: 30,
    };
  }

  const verifications = data.verifications || {};
  const freeUsed = verifications.free_used || 0;
  const freeLimit = verifications.free_limit || 1000;
  const percentageUsed = freeLimit > 0 ? (freeUsed / freeLimit) * 100 : 0;

  return {
    isLoading: false,
    error: null,
    plan: data.current_plan,
    freeUsed,
    freeLimit,
    paidUsed: verifications.paid_used || 0,
    totalUsed: verifications.total || freeUsed,
    percentageUsed,
    isNearLimit: percentageUsed >= 75 && percentageUsed < 100,
    isAtLimit: percentageUsed >= 100,
    isOverLimit: freeUsed > freeLimit,
    overageRate: data.overage_rate_cents,
    estimatedOverage: data.estimated_overage_dollars,
    daysRemaining: data.billing_cycle?.days_remaining || 0,
    hasPaymentMethod: data.has_payment_method,
    subscriptionStatus: data.subscription_status,
  };
}

// ===========================================
// PLAN FEATURE CONSTANTS
// ===========================================

/**
 * Feature names for use with useHasFeature hook
 */
export const FEATURES = {
  WORKFLOW_BUILDER: 'workflow_builder',
  CASES: 'cases',
  AUDIT_LOGS: 'audit_logs',
  WEBHOOKS: 'webhooks',
  AI_BATCH_ANALYZE: 'ai_batch_analyze',
  CUSTOM_BRANDING: 'custom_branding',
  SSO: 'sso',
  MAX_TEAM_MEMBERS: 'max_team_members',
  MAX_API_KEYS: 'max_api_keys',
};

/**
 * Plan tiers in order of capability
 */
export const PLAN_TIERS = ['developer', 'starter', 'professional', 'enterprise'];

/**
 * Get the minimum plan required for a feature
 *
 * @param {string} feature - Feature name
 * @returns {string} Minimum plan name
 */
export function getMinimumPlanForFeature(feature) {
  const featurePlans = {
    [FEATURES.WORKFLOW_BUILDER]: 'professional',
    [FEATURES.CASES]: 'professional',
    [FEATURES.AUDIT_LOGS]: 'professional',
    [FEATURES.WEBHOOKS]: 'starter',
    [FEATURES.AI_BATCH_ANALYZE]: 'starter',
    [FEATURES.CUSTOM_BRANDING]: 'professional',
    [FEATURES.SSO]: 'enterprise',
  };

  return featurePlans[feature] || 'professional';
}

/**
 * Get upgrade CTA based on current plan and desired feature
 *
 * @param {string} currentPlan - Current plan
 * @param {string} feature - Desired feature
 * @returns {Object} { targetPlan, ctaText, isContactSales }
 */
export function getUpgradeCTA(currentPlan, feature) {
  const minPlan = getMinimumPlanForFeature(feature);
  const currentIndex = PLAN_TIERS.indexOf(currentPlan);
  const minIndex = PLAN_TIERS.indexOf(minPlan);

  if (currentIndex >= minIndex) {
    return { targetPlan: null, ctaText: null, isContactSales: false };
  }

  if (minPlan === 'enterprise') {
    return {
      targetPlan: 'enterprise',
      ctaText: 'Contact Sales',
      isContactSales: true,
    };
  }

  return {
    targetPlan: minPlan,
    ctaText: `Upgrade to ${minPlan.charAt(0).toUpperCase() + minPlan.slice(1)}`,
    isContactSales: false,
  };
}

export default usePlanFeatures;
